"use client"

import { useState, useEffect, useCallback, useRef } from "react"

interface AnchorItem {
  anchorId: string
  anchorLinkText: string
}

interface JumpNavProps {
  anchors: AnchorItem[]
}

const FALLBACK_TOP = 32

export default function JumpNav({ anchors }: JumpNavProps) {
  const [activeId, setActiveId] = useState<string>("")
  const [stickyTop, setStickyTop] = useState(FALLBACK_TOP)
  const stickyRef = useRef<HTMLDivElement>(null)

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, anchorId: string) => {
      e.preventDefault()
      const el = document.getElementById(anchorId)
      if (el) {
        const navEl = document.querySelector(
          ".site-navigation",
        ) as HTMLElement | null
        const navVisible =
          navEl && !navEl.classList.contains("site-navigation--is-hidden")
        const offset = navVisible ? -(navEl!.offsetHeight + 16) : -80
        const y = el.getBoundingClientRect().top + window.scrollY + offset
        window.scrollTo({ top: y, behavior: "smooth" })
      }
    },
    [],
  )

  useEffect(() => {
    const navEl = document.querySelector(
      ".site-navigation",
    ) as HTMLElement | null
    if (!navEl) return

    const update = () => {
      const isHidden = navEl.classList.contains("site-navigation--is-hidden")
      const navHeight = isHidden ? 0 : navEl.offsetHeight
      setStickyTop(navHeight + 16)
    }

    update()

    const classObserver = new MutationObserver(update)
    classObserver.observe(navEl, {
      attributes: true,
      attributeFilter: ["class"],
    })

    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(navEl)

    return () => {
      classObserver.disconnect()
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const anchorIds = anchors.map(a => a.anchorId)
    const elements = anchorIds
      .map(id => document.getElementById(id))
      .filter(Boolean) as HTMLElement[]

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 },
    )

    elements.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [anchors])

  if (!anchors?.length) return null

  return (
    <nav aria-label="Jump navigation" className="hidden lg:block w-52 shrink-0">
      <div
        ref={stickyRef}
        className="sticky transition-[top] duration-300 ease-in-out"
        style={{ top: `${stickyTop}px` }}
      >
        <ul className="border-l border-gray-300">
          {anchors.map((anchor, index) => {
            const isActive = activeId === anchor.anchorId
            const isFirst = index === 0
            return (
              <li key={anchor.anchorId}>
                <a
                  href={`#${anchor.anchorId}`}
                  onClick={e => handleClick(e, anchor.anchorId)}
                  className={`t2-jump-nav__link -ml-px block border-l-[3px] py-1.5 pl-3 transition-colors duration-150 ${
                    isFirst
                      ? `text-sm font-bold text-gray-800 ${isActive ? "border-blue-trmb" : "border-blue-trmb"}`
                      : `text-[13px] leading-snug ${
                          isActive
                            ? "border-blue-trmb text-blue-trmb font-medium"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-400"
                        }`
                  }`}
                >
                  {anchor.anchorLinkText}
                </a>
              </li>
            )
          })}
        </ul>
      </div>
    </nav>
  )
}
